import axios from 'axios';
import { API_URL } from './constants';

// Connection status tracking
let isBackendOnline = true;

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second
const CONNECTION_TIMEOUT = 5000; // 5 seconds

/**
 * Check if backend is online by pinging health endpoint
 */
export const checkBackendHealth = async () => {
  try {
    const baseUrl = API_URL.replace('/api', '');
    const response = await axios.get(`${baseUrl}/health`, {
      timeout: CONNECTION_TIMEOUT,
      validateStatus: (status) => status < 500 // Accept 200-499 as "online"
    });
    isBackendOnline = true;
    return { online: true, status: response.status };
  } catch (error) {
    isBackendOnline = false;
    return { online: false, error: error.message };
  }
};

/**
 * Get current backend status
 */
export const isBackendAvailable = () => isBackendOnline;

// Create axios instance with custom configuration
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  async (config) => {
    // Add auth token if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor with error handling
api.interceptors.response.use(
  (response) => {
    // Request succeeded
    isBackendOnline = true;
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Network error or connection timeout
    if (
      !error.response &&
      (error.message === 'Network Error' ||
        error.code === 'ECONNABORTED' ||
        error.code === 'ERR_NETWORK' ||
        error.message.includes('timeout'))
    ) {
      isBackendOnline = false;
      
      // Don't retry login requests
      if (originalRequest.url?.includes('/auth/login')) {
        return Promise.reject({
          ...error,
          message: 'ಸರ್ವರ್ ಸಂಪರ್ಕವಿಲ್ಲ. ದಯವಿಟ್ಟು ನಂತರ ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
          isNetworkError: true,
        });
      }

      // Retry other requests
      if (!originalRequest._retry && (originalRequest._retryCount || 0) < MAX_RETRIES) {
        originalRequest._retry = true;
        originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;

        try {
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY * originalRequest._retryCount));
          return await api(originalRequest);
        } catch (retryError) {
          return Promise.reject({
            ...retryError,
            message: 'ಸರ್ವರ್ ಸಂಪರ್ಕವಿಲ್ಲ. ದಯವಿಟ್ಟು ನಂತರ ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
            isNetworkError: true,
          });
        }
      }

      return Promise.reject({
        ...error,
        message: 'ಸರ್ವರ್ ಸಂಪರ್ಕವಿಲ್ಲ. ದಯವಿಟ್ಟು ನಂತರ ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
        isNetworkError: true,
      });
    }

    // 401 Unauthorized - token expired or invalid
    if (error.response?.status === 401) {
      // Don't redirect on login page
      if (originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/me')) {
        localStorage.removeItem('token');
        delete api.defaults.headers.common['Authorization'];
        return Promise.reject(error);
      }

      // Redirect to login for other requests
      localStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    // 500 Server Error - might be temporary
    if (error.response?.status === 500 && !originalRequest._retry) {
      originalRequest._retry = true;
      originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;

      if (originalRequest._retryCount < 2) {
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
        try {
          return await api(originalRequest);
        } catch (retryError) {
          return Promise.reject(retryError);
        }
      }
    }

    return Promise.reject(error);
  }
);

export default api;

